import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";
import {
  FieldHelperProps,
  FieldInputProps,
  FieldMetaProps,
  useFormikContext,
} from "formik";
import { ClassNameMap, InputAdornment, TextField } from "@mui/material";

import { GenericObject } from "../../services/Price";
import { TooltipLabel } from "../../components/Tooltips";
import {
  ConfigurationFormType,
  EditFormDetails,
  WatchFieldType,
} from "../interfaces";
import { reducePath, useMaxFromApi } from "../utils";
import { currentUserCurrencyAtom } from "../../AuthProvider";

const getValueFromWatchedField = (
  values: GenericObject,
  valuePath?: string | WatchFieldType
) => {
  if (valuePath && typeof valuePath === "string") {
    const watchedFieldParamsPath = valuePath.split(".");
    const watchedFieldParamsValue = reducePath(values, watchedFieldParamsPath);

    return watchedFieldParamsValue;
  }

  return null;
};

/**
 *
 * @returns
 */
const InputTextField = ({
  classes,
  field,
  filter,
  helpers,
  meta,
}: {
  classes: ClassNameMap;
  field: FieldInputProps<any>;
  filter: EditFormDetails;
  helpers: FieldHelperProps<any>;
  meta: FieldMetaProps<any>;
}) => {
  const intl = useIntl();
  const currency = useRecoilValue(currentUserCurrencyAtom);
  const { values, setFieldValue } = useFormikContext<GenericObject>();
  const watchedFieldValue = getValueFromWatchedField(
    values,
    filter.watchFieldToFill
  );

  useEffect(() => {
    if (watchedFieldValue) {
      setFieldValue(field.name, watchedFieldValue);
    }
  }, [watchedFieldValue, field.name, setFieldValue]);

  const maxValue = useMaxFromApi(values, field.name, filter.maxFromApi);

  return (
    <TextField
      {...field}
      multiline={filter.type === ConfigurationFormType.TextArea}
      rows={filter.type === ConfigurationFormType.TextArea ? 4 : undefined}
      className={classes.inputText}
      variant="outlined"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        if (filter.inputReplacer) {
          return helpers.setValue(filter.inputReplacer(e.target.value));
        }

        return helpers.setValue(e.target.value);
      }}
      disabled={
        filter.readonly ||
        (values &&
          !!filter.enableField &&
          !Boolean(values[filter.enableField as keyof GenericObject]))
      }
      fullWidth
      slotProps={{
        htmlInput:
          maxValue !== undefined && maxValue !== null ? { max: maxValue } : {},
        input: {
          ...filter?.inputProps,
          endAdornment:
            filter.inputType === "currency" ? (
              <InputAdornment position="end">{currency.symbol}</InputAdornment>
            ) : maxValue !== undefined && maxValue !== null ? (
              <InputAdornment position="end">/ {maxValue}</InputAdornment>
            ) : (
              filter.inputProps?.endAdornment
            ),
        },
      }}
      label={
        filter.tooltip ? (
          <TooltipLabel
            label={intl.formatMessage({ id: filter.label })}
            tooltipLabel={intl.formatMessage(
              {
                id: filter.tooltip,
              },
              { br: <br /> }
            )}
            translateTooltip={false}
          />
        ) : (
          intl.formatMessage({ id: filter.label })
        )
      }
      error={meta.touched && !!meta.error}
      helperText={meta.touched && meta.error}
      value={field.value || field.value === 0 ? field.value : ""}
    />
  );
};

export default InputTextField;
